'use client';

import { useEffect, useRef, useState } from 'react';
import { usePrefersReducedMotion } from '@/lib/usePrefersReducedMotion';

/**
 * A full-viewport scrim that covers a hand-off.
 *
 * Two places need to hide the same moment: the landing CTA, where the next
 * route is still loading, and the campaign hook, where continuing mounts the
 * whole editor shell and its 3D canvas at once. Both looked abrupt for the
 * same reason — one composition is replaced by a completely different one in
 * a single frame — and both are fixed the same way, so this is one component
 * rather than two that would slowly disagree about the duration.
 *
 * ## Why it works, and why the colour is not a detail
 *
 * `--app-bg` is the same token the editor paints and the same value the 3D
 * canvas clears to (`STAGE` in scene/constants.ts). So a scene that has not
 * drawn its geometry yet is INDISTINGUISHABLE from the curtain: what lifts is
 * a room appearing on a ground that was already there, not a new surface
 * arriving. That is what removes the pop, more than the fade does. Overriding
 * `color` breaks that guarantee; do it only when covering something that is
 * genuinely a different surface.
 *
 * ## Reduced motion
 *
 * The curtain still covers, it just stops animating. Someone who asked for
 * less motion has not asked to see a raw component swap — the abrupt frame is
 * the thing they would most notice. So the duration collapses to zero and the
 * scrim snaps, which is both honest to the setting and still does the job.
 */
export const CURTAIN_MS = 320;

/** How long to wait for an animation frame before assuming the tab is hidden
 *  and painting anyway. Comfortably longer than a real frame. */
const HIDDEN_TAB_PAINT_MS = 120;

/** Grace on the settle timer, so `transitionend` gets to win in the normal
 *  case and the timer is only ever the backstop. */
const SETTLE_SLACK_MS = 60;

export default function Curtain({
  shown,
  durationMs = CURTAIN_MS,
  color = 'var(--app-bg)',
  onSettled,
}: {
  /** Drive this to fade the scrim in and out. */
  shown: boolean;
  durationMs?: number;
  /** Defaults to the app background, which is also the 3D clear colour. */
  color?: string;
  /** Fired after each transition finishes, with the state it settled into.
   *  Callers sequence on this rather than on a timer of their own, so the
   *  duration lives in exactly one place. */
  onSettled?: (shown: boolean) => void;
}) {
  // First paint must be transparent or there is nothing to animate from: a
  // component that mounts already at its target opacity simply appears. The
  // rAF lets that transparent frame land before the real value is applied.
  //
  // The timeout beside it is not belt-and-braces. A BACKGROUND tab does not
  // run animation frames at all, so a viewer who clicks continue and switches
  // away would leave `painted` false forever, the scrim transparent, and the
  // caller's swap never sequenced. rAF wins every time in a visible tab, at
  // roughly 16ms against this 120ms, so the transparent frame is preserved
  // exactly where it matters.
  const [painted, setPainted] = useState(false);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const frame = requestAnimationFrame(() => setPainted(true));
    const fallback = setTimeout(() => setPainted(true), HIDDEN_TAB_PAINT_MS);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(fallback);
    };
  }, []);

  const effectiveMs = reducedMotion ? 0 : durationMs;
  const opacity = painted && shown ? 1 : 0;

  // Settle on a timer as well as on `transitionend`, whichever comes first.
  //
  // `transitionend` is not a promise the browser keeps. It never fires when
  // the duration is zero (reduced motion), nor in a background tab, nor if
  // the transition is interrupted — and the caller here sequences a component
  // swap on it, so a missed event does not degrade the animation, it strands
  // the visitor behind an opaque scrim. The timer is the guarantee and the
  // event is the optimisation.
  // A ref, not state: the settle timer's closure is created on one render and
  // fires on a later one, so a state-based guard reads the value as it was
  // when the timer was scheduled -- always null -- and both sources fire. A
  // caller swapping twice would mount the shell over itself.
  const settledForRef = useRef<boolean | null>(null);
  const settle = (state: boolean) => {
    if (settledForRef.current === state) return;
    settledForRef.current = state;
    onSettled?.(state);
  };

  useEffect(() => {
    if (!painted) return;
    const id = setTimeout(() => settle(shown), effectiveMs + SETTLE_SLACK_MS);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [painted, shown, effectiveMs]);

  // A new target means the previous settle no longer applies. Written during
  // render, the sanctioned "store info from previous renders" pattern, so the
  // reset is visible to a timer scheduled by the effect below on this very
  // render rather than one commit late.
  const lastShownRef = useRef(shown);
  if (lastShownRef.current !== shown) {
    lastShownRef.current = shown;
    settledForRef.current = null;
  }

  return (
    <div
      aria-hidden
      data-testid="curtain"
      data-shown={shown ? 'true' : 'false'}
      onTransitionEnd={(e) => {
        // Only this element's own opacity. A transition bubbling up from
        // anything the caller rendered underneath would settle the sequence
        // early.
        if (e.target === e.currentTarget && e.propertyName === 'opacity') {
          settle(shown);
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: color,
        opacity,
        // A curtain is a change of light level, not an object settling, so
        // `linear` — the same easing every other scrim in the product uses.
        transition: `opacity ${effectiveMs}ms linear`,
        zIndex: 100,
        pointerEvents: 'none',
      }}
    />
  );
}
